import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n/i18n";

// Downscales images to keep localStorage usage small; SVG is stored as-is.
const readImage = (file, max, type) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = reject;
  r.onload = () => {
    if (file.type === "image/svg+xml") { resolve(r.result); return; }
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const k = Math.min(1, max / img.width);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL(type, 0.8));
    };
    img.src = r.result;
  };
  r.readAsDataURL(file);
});

export const ImageUpload = ({ id, label, hint, value, onChange, max, type, fit }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const { t } = useI18n();
  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("Formato no válido. Usa PNG, JPG o SVG.")); return; }
    onChange(await readImage(file, max, type));
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t(label)}</Label>
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
        className={`flex h-40 w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-colors ${drag ? "border-upv bg-upv-light" : value ? "border-slate-200 bg-white" : "border-slate-300 bg-slate-50 hover:border-upv"}`}
        data-testid={`company-reg-${id}-dropzone`}
      >
        {value ? (
          <img src={value} alt={t(label)} className={fit === "contain" ? "max-h-full max-w-full object-contain p-5" : "h-full w-full object-cover"} data-testid={`company-reg-${id}-preview`} />
        ) : (
          <span className="flex flex-col items-center gap-2 px-4 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-upv shadow-sm"><ImagePlus className="h-5 w-5" /></span>
            <span className="font-display text-sm font-bold tracking-wider">{t("SUBIR IMAGEN")}</span>
            <span className="text-xs text-slate-500">{t(hint)}</span>
          </span>
        )}
      </button>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }} data-testid={`company-reg-${id}-input`} />
      {value && (
        <div className="flex gap-4 text-xs font-semibold">
          <button type="button" onClick={() => input.current?.click()} className="text-slate-600 underline-offset-4 hover:underline" data-testid={`company-reg-${id}-change`}>{t("Cambiar imagen")}</button>
          <button type="button" onClick={() => onChange(null)} className="text-rose-600 underline-offset-4 hover:underline" data-testid={`company-reg-${id}-remove`}>{t("Eliminar")}</button>
        </div>
      )}
    </div>
  );
};
